import {
    ChangeDetectionStrategy,
    Component,
    Input,
    Self,
    OnInit,
    ChangeDetectorRef,
} from '@angular/core';
import { FormControl, NgControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { merge } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { BazisFormService } from '@bazis/form/services/form.service';
import { BazisConfigurationService } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'app-input-point',
    templateUrl: './input-point.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppInputPointComponent extends BazisControlValueAccessor implements OnInit {
    @Input() latTitleKey: string = null;

    @Input() lngTitleKey: string = null;

    @Input() latPlaceholderKey: string = null;

    @Input() lngPlaceholderKey: string = null;

    @Input() precision: number = 6;

    @Input()
    anchorRoot = '';

    lat = new FormControl();

    lng = new FormControl();

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        configurationService: BazisConfigurationService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);
        }

        merge(this.lat.valueChanges, this.lng.valueChanges)
            .pipe(
                debounceTime(0),
                tap((v) => {
                    const lat = (+this.lat.value).toFixed(this.precision);
                    const lng = (+this.lng.value).toFixed(this.precision);
                    if (!+lat && !+lng) return;
                    this.change(lat, lng);
                }),
                untilDestroyed(this),
            )
            .subscribe();
        merge(this.required$)
            .pipe(
                tap((v) => {
                    this.lat.setValidators(v ? Validators.required : []);
                    this.lng.setValidators(v ? Validators.required : []);
                    this.lat.updateValueAndValidity();
                    this.lng.updateValueAndValidity();
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.lat.disable();
            this.lng.disable();
        } else {
            this.lat.enable();
            this.lng.enable();
        }
    }

    change(lat, lng) {
        this.onChange({
            type: 'Point',
            coordinates: [+lng, +lat],
        });
    }

    public writeValue(value: any): void {
        if (value && !value.coordinates[1] && !value.coordinates[0]) {
            value = null;
        }
        const lat = value ? (+value.coordinates[1]).toFixed(this.precision) : null;
        const lng = value ? (+value.coordinates[0]).toFixed(this.precision) : null;
        this.lat.setValue(lat, { emitEvent: false });
        this.lng.setValue(lng, { emitEvent: false });
        this.lat.updateValueAndValidity();
        this.lng.updateValueAndValidity();
    }
}
